/**
 * Debounced search input. Fires `onSearch(value)` after `delayMs` of
 * inactivity — the surrounding page owns the canonical search string
 * (used as the React Query key) so the input stays purely presentational.
 *
 * The clear `×` button only resets the visible text. The parent decides
 * what to do with an empty string (today: call `onSearch('')` so the
 * server hits the unfiltered list).
 */
import { useEffect, useState, type ChangeEvent } from 'react';

interface SearchInputProps {
  value: string;
  onSearch: (next: string) => void;
  placeholder?: string;
  delayMs?: number;
  /** Optional className applied to the wrapping <div>. */
  className?: string;
}

export function SearchInput({
  value,
  onSearch,
  placeholder = 'Search…',
  delayMs = 300,
  className,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value);

  // Keep the visible draft in sync if the canonical value is reset
  // externally (e.g. parent clears on route change).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Debounce the prop callback. Resets whenever `draft` or `delayMs`
  // changes so the latest input wins.
  useEffect(() => {
    if (draft === value) return;
    const handle = setTimeout(() => onSearch(draft), delayMs);
    return () => clearTimeout(handle);
  }, [draft, delayMs, onSearch, value]);

  return (
    <div className={`feluda-search ${className ?? ''}`.trim()}>
      <input
        type="search"
        className="feluda-search-field"
        placeholder={placeholder}
        value={draft}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        aria-label="Search repositories"
      />
      {draft && (
        <button
          type="button"
          className="feluda-search-clear"
          aria-label="Clear search"
          onClick={() => setDraft('')}
        >
          ×
        </button>
      )}
    </div>
  );
}
